import './assets/index.css'
import { createApp, nextTick } from 'vue'
import { createPinia } from 'pinia'
import { createI18n } from 'vue-i18n'
import 'preline/preline'

// Import Froala Editor
import 'froala-editor/js/plugins.pkgd.min.js'
import 'froala-editor/js/third_party/font_awesome.min'
import 'froala-editor/js/third_party/image_tui.min'
import 'froala-editor/js/languages/de.js'
import 'froala-editor/css/froala_editor.pkgd.min.css'
import 'froala-editor/css/froala_style.min.css'
import VueFroala from 'vue-froala-wysiwyg'

import App from './App.vue'
import router from './router'
import posthog from 'posthog-js'
import type { PostHog } from 'posthog-js'
import setupPdfs from './utils/pdfjs'
import { setupApiV2, setupApiV3, setupSentry, setupTranslations } from '@/utils/bootstrap'

declare global {
  interface Window {
    posthog?: PostHog
  }
}

const app = createApp(App)
const i18n = createI18n({ legacy: false })
const API_URL = import.meta.env.VITE_API_URL
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN
const ENV = import.meta.env.VITE_ENV

app.use(createPinia())
app.use(router)

// Set up API clients
setupApiV3({ apiUrl: API_URL })
setupApiV2({ apiUrl: API_URL })

// Set up Sentry error tracking with PostHog integration
setupSentry({
  app,
  router,
  dsn: SENTRY_DSN,
  environment: ENV,
  release: import.meta.env.VITE_SENTRY_RELEASE,
  additionalIntegrations: [
    posthog.sentryIntegration({
      organization: import.meta.env.SENTRY_ORGANIZATION,
      projectId: import.meta.env.SENTRY_PROJECT_ID,
      severityAllowList: ['error', 'info']
    })
  ]
})

// Set up translations
setupTranslations({ app, i18n })
app.use(VueFroala)

setupPdfs()

app.mount('#app')

/**
 * Capture SPA page views via PostHog.
 *
 * PostHog is already initialized, identified, and grouped in the Django
 * base template (base.html) using server-rendered user/org data. This
 * avoids race conditions where page views fire before the user or
 * organisation has been identified.
 *
 * The SPA only needs to capture additional $pageview events for
 * client-side (Vue Router) navigations that don't trigger a full
 * page load. The initial page view is already captured by base.html,
 * so we skip the first navigation (where from has no matched routes).
 */
router.afterEach((to, from, failure) => {
  if (!failure && from.matched.length > 0) {
    nextTick(() => {
      window.posthog?.capture('$pageview', { path: to.fullPath })
    })
  }
})
